// 盤面視圖：6 主輪 + 1 條 Tracker 頂輪（疊加於主輪 1-4 上方，見 constants.ts 說明）。
//
// 版面配置照 Unity LandscapeUI.prefab 精確還原（設計座標 1920×1080，見下方 DESIGN_* 常數）：
//   - 底板框由兩張美術圖左右鏡像拼成：`pic_stageBg`（輪軸凹槽背景，墊底）＋ `pic_stage`（裝飾邊框，前景）。
//   - `pic_pillar` 只是頂輪與主輪之間的細分隔橫條（非外框四邊）。
//   - 格子固定 214:134 比例、無間距；整組框架以單一 scale 等比縮放置中，故美術框與格點永遠吻合。
//
// 轉動揭示（playSpin）— 對照 Unity「全輪皆 drop 型（_isDrop=1）」的實際流程（非捲動轉輪）：
//   ① 舊符號整欄墜落離場（ClearSymbolsSequential：0.3s InQuad、[使用者要求] 整欄同時掉、逐欄 0.1s 錯開；Tracker 向左滑出）。
//   ② 保底空窗 0.4s（ReelControllerMediator._spinDuration，Turbo 也不縮短）。
//   ③ 結果符號自上方掉入（FillSymbols/DoFillEffect：單顆 0.4s 三段「落 0.32 InQuad → 上彈 20px 0.04 → 落定 0.04」、
//      [使用者要求] 整欄同時掉、逐欄 0.1s；Tracker 與主輪第 0 欄同組、由右往左滑入）。欄內由底列往頂列依序堆疊。
//   ⇒ 轉動揭示（①③）整欄一起掉入、只保留欄間左→右順序；消除補位（playFill）仍維持逐顆 0.05s（見 fillReelColumn 的 staggerCells）。
//   聽牌 Omen（僅主遊戲）：已落地 2 顆 Scatter 起、達 3 顆止，該欄改逐顆 0.4s 慢速掉入＋ani_draw 光柱＋se_draw loop（不受 staggerCells 影響、恆逐顆）。
//   快停（forceStopRequested）：略過所有逐欄等待（掉落 tween 本身照播），對應 Unity ForceStopRotation。
//   Turbo：離場的逐欄錯開略過（同 Unity）；落定的逐欄改為一半（刻意偏離 Unity，
//     Unity 的 Turbo 是借用快停路徑而全部歸零＝同時落定）。依據見 staggerMs 家族與 TURBO_STAGGER_SCALE。
//
// 消除連鎖演出拆為四個原語（由 GameController 依 Unity DestroySymbol→FillSymbol→TransformSymbol 時序驅動）：
//   maskLosers/unmaskLosers（非中獎 0.502 灰）→ playWinAnimations（win loop 1.5s→remove 爆散）→
//   playCollapse（存活符號 0.4s OutBounce 同時下墜）→ playFill（補牌，含補牌預兆 ani_reel_bg＋1.5s 拉張力）→
//   playWildTransforms（Wild 變身＋ani_wildFrame 金木框）。浮動計分（WinTemp）位置見 WIN_TEMP_AY。

import { Container, Graphics, Rectangle, Sprite } from 'pixi.js'
import type { Spine } from '@esotericsoftware/spine-pixi-v8'
import {
  CHANGE_SYMBOL_DELAY_MS,
  FILL_BOUNCE_DESIGN_PX,
  FILL_DURATION_S,
  FILL_INTERVAL_S,
  FILL_OMEN_DELAY_MS,
  FILL_OMEN_DELAY_TURBO_MS,
  DROP_DURATION_S,
  MAIN_REEL_COUNT,
  OMEN_MAX_SCATTER,
  ANTICIPATION_SCATTER_COUNT,
  REEL_EXIT_DURATION_S,
  REEL_SPIN_DURATION_S,
  REEL_START_INTERVAL_S,
  REEL_STOP_INTERVAL_S,
  SCATTER_SYMBOL_ID,
  SCATTER_WOLF_MAX_LEVEL,
  TRACKER_OVERLAY_REELS,
  TRACKER_REEL_INDEX,
  trackerReelForPosition,
  TURBO_STAGGER_SCALE,
  VISIBLE_ROWS,
  WIN_LOOP_HOLD_MS,
  WIN_TEMP_COUNT_S,
} from '../constants'
import { delay } from '../../framework/core/async'
import { formatMoney } from '../../framework/core/format'
import {
  unityAnchorLandscape,
  unityAnchorPortrait,
  unityScaleLandscape,
  unityScalePortrait,
  type Orientation,
  type Resizable,
} from '../../framework/render/design'
import type { EventBus } from '../../framework/events/EventBus'
import type { GameEventMap } from '../events/GameEvents'
import { cellSpan, cellViewSymbol, isCovered, type Board, type CellRef, type ReelColumn } from '../model/board'
import type { TumbleStep } from '../logic/tumbleEngine'
import { getUiTexture } from '../assets/UiTextures'
import { createEffectSpine, EFFECT_ANIM, animationDuration, hasAnimation } from '../assets/EffectSpines'
import { BitmapNumber } from './BitmapNumber'
import { SymbolView } from './SymbolView'
import { killTweensOf, tweenTo } from './tween'

// ── Unity 設計座標常數（1920×1080，皆為中心座標；螢幕換算見 sx/sy） ──
const DESIGN_CX = 960 // 設計空間水平中心
const FRAME_CY = 512 // pic_stage/pic_stageBg 兩半合併後的中心 y
const FRAME_HALF_W = 744 // 每半框架圖原生寬
const FRAME_HALF_H = 976 // 每半框架圖原生高
const FRAME_HALF_OFFSET = 372 // 左右半各自中心距 DESIGN_CX 的距離
const CELL_W_REF = 214 // 單格設計寬
const CELL_H_REF = 134 // 單格設計高
const MAIN_COL0_X = 425 // 主輪第 0 欄中心 x（design）
const MAIN_ROW0_Y = 320 // 主輪第 0 列中心 y（design）
const TRACKER_ROW_Y = 165 // Tracker 頂輪列中心 y（design）
const PILLAR_Y = 240 // 分隔柱中心 y（design）
const PILLAR_W = 858
const PILLAR_H = 20
// 遮罩區域（design，左上原點 + 寬高）：主盤面 6×5 可視窗、Tracker 頂輪可視窗。
const MAIN_MASK = { x: 318, y: 253, w: 1284, h: 670 } as const
const TRACKER_MASK = { x: 522, y: 88, w: 876, h: 154 } as const

/**
 * Wild 變身金木框（ani_wildFrame，Unity 節點 ChangeWildFrame）——**不與 preWin 等一般整版 Spine 共用
 * layoutBoardSpine 的置中**：Unity SkeletonGraphic 的 pivot（＝骨骼原點）不在可見框正中，而在幾何中心
 * 下方約 23.5px（pivot.y=0.47225），且節點自身另有 anchoredPosition。若沿用 FRAME_CY 置中，骨骼原點會
 * 被放到「應是可見中心」的位置，使整個框上移約 24 設計 px（＝使用者回報的「金框偏高、沒對上 UI」）。
 * 改以骨骼原點相對畫面中心（Unity y-up）直接定位：
 *   橫版 (0, 3.7)＝Board 中心 aPos(0,50) ＋ 節點 aPos(0,−46.3)；
 *   直版 (0, 82.3)＝Board 中心 aPos(0,65.3) ＋ 節點 aPos(0,17)，節點另 localScale 0.8（已含在 scaleFactor）。
 * 權威值：LandscapeUI.prefab / PoriraitUI.prefab 的 ChangeWildFrame（SkeletonGraphic referenceSize 913×847）。
 */
const WILD_FRAME_UY_LANDSCAPE = 3.7
const WILD_FRAME_UY_PORTRAIT = 82.3

/**
 * 浮動計分（WinTemp）位置：Unity 的 WinTemp 是獨立共用 prefab（`SlotFramework/Prefab/UI/Game/WinTemp.prefab`），
 * 掛在 BottomBar 節點下、**以畫面座標定位而非盤面座標**，anchoredPosition 橫版 (0, −340)（LandscapeUI 覆寫）、
 * 直版 (0, −200)（prefab 原值）＝畫面中心往下，落在最底排符號一帶（非盤面正中）。
 * 與主倍率飛行終點 EndDummy（橫 (0,−340)／直 (0,−199)）重合，見 MultiplierView。
 */
const WIN_TEMP_AY = -340
const WIN_TEMP_P_AY = -200

/**
 * 浮動計分（WinTemp）版面（同上 prefab，1920×1080 設計 px）：
 * 根節點固定高 115、掛 HorizontalLayoutGroup（左右 padding 各 80、spacing 0、MiddleCenter）
 * ＋ ContentSizeFitter(Horizontal=Preferred) → **底板寬＝160＋數字寬**、高恆為 115。
 * 底板 Image ＝ `pic_marquee_2`（Simple 拉伸）染黑、alpha 0.8627451。
 * 數字用 `tx_multiple` 位圖字、TMP fontSize 60 搭 Empty 字型（pointSize 71、ascentLine 64.274）；
 * TMP 會把每個 sprite glyph 正規化到 ascentLine 高 → 實際字高 64.274×60/71 = 54.32 設計 px。
 * 本移植的 BitmapNumber 是「整套字型最高 glyph 統一縮放」（tx_multiple 逗號 72、數字 70），
 * 故目標高取 54.32×72/70，讓佔多數的數字剛好等於 Unity 的 54.32（逗號則高 2.8%，肉眼無感）。
 * 字距＝TMP characterSpacing −10，換算 −10×0.01×fontSize = −6 設計 px（實測可還原 prefab 序列化的
 * 「80.00」寬 164.56：raw advance 188.56 − 4 個字間距×6）。
 * [已知差異] prefab 第二個 TMP（" X " 運算式欄位）本移植未做——`WinTemp.Awake()` 一律先清空它，
 * 而 Slot026 從未發 ShowFormula/SettleWinTemp 事件，故執行期恆為空字串、不佔寬度。
 */
const WIN_TEMP_BAND_H = 115
const WIN_TEMP_PAD_X = 160
const WIN_TEMP_TEXT_H = (64.274414 * 60) / 71 / (70 / 72)
const WIN_TEMP_SPACING_RATIO = -6 / WIN_TEMP_TEXT_H
const WIN_TEMP_BG_ALPHA = 0.8627451

/** 補位掉落三段時長比例（Unity DoFillEffect：0.8 落下 / 0.1 上彈 / 0.1 落定）。 */
const FILL_SEG_DROP = 0.8
const FILL_SEG_BOUNCE = 0.1

export class ReelsView extends Container implements Resizable {
  // 底板框（左右鏡像兩半）：stageBg 墊底、stage 前景邊框。
  private readonly stageBgLeft = new Sprite(getUiTexture('stageBg'))
  private readonly stageBgRight = new Sprite(getUiTexture('stageBg'))
  private readonly stageFrontLeft = new Sprite(getUiTexture('stage'))
  private readonly stageFrontRight = new Sprite(getUiTexture('stage'))
  /** 頂輪與主輪間的細分隔橫條。 */
  private readonly pillar = new Sprite(getUiTexture('frameTrim'))

  private readonly cells: SymbolView[][] = []
  private readonly trackerCells: SymbolView[] = []
  /** 主輪掉落動畫用的暫時分身圖層（遮罩裁切至主盤面可視窗）。 */
  private readonly fallLayer = new Container()
  /** Tracker 頂輪掉落動畫用的暫時分身圖層（遮罩裁切至頂輪可視窗）。 */
  private readonly fallTrackerLayer = new Container()
  private readonly fallMaskMain = new Graphics()
  private readonly fallMaskTracker = new Graphics()
  /** 聽牌／補牌預兆光柱的主盤面遮罩（單一矩形，同 fallMaskMain 手法，走 scissor 裁切）。 */
  private readonly omenMask = new Graphics()
  /**
   * 聽牌（ani_draw "draw_down"）／補牌預兆（ani_reel_bg 底光）Spine 圖層：作為欄位「背景光」，
   * 刻意排在符號之下（見 constructor z-order），並比照符號掉落分身以 omenMask 裁切至主盤面可視窗——
   * 讓它像 symbol 一樣不溢出盤面、且襯在符號後方而非蓋住符號。
   */
  private readonly omenLayer = new Container()
  /** Wild 變身金木框（ani_wildFrame，蓋在最上）＋盤面中央浮動計分。 */
  private readonly overlayLayer = new Container()

  /** Turbo 加速開關：略過逐顆/逐欄錯開等待（掉落 tween 本身不縮短，對照 Unity ForceStop 行為）。 */
  private turboEnabled = false
  /** 免費遊戲模式：FG 中 Scatter 顯示野狼分級圖、且不做 Scatter 聽牌預兆（對照 Unity !IsFreeGame 守衛）。 */
  private freeGameActive = false
  /** FG 中 Scatter 顯示的野狼分級（1~SCATTER_WOLF_MAX_LEVEL）；由 GameController 依累積計量等級設定。 */
  private freeGameScatterLevel = 1
  /**
   * 目前「已顯示盤面」是用哪個野狼等級渲染的（0＝一般 SS）。供轉動開始清舊符號（exitOldSymbols）時，
   * 讓舊符號沿用「當時顯示的等級」而非最新等級——這樣觸發 FG 的 Scatter 在 FG 第一手仍以一般 SS 離場
   * （對照 Unity：野狼等級只在該手停輪建盤才寫入，觸發盤面第一手前維持一般 SS）。
   */
  private lastBoardScatterLevel = 0
  /**
   * FG 中每格 Scatter 的野狼臉等級（key=`${reel}:${row}`／`t:${i}`），對照 Unity Trans2BoardData 的
   * cellData.Level = Random.Range(1, min(level+1, 4))——每格獨立隨機 1..min(tier,3)、同一份盤面內穩定；
   * 盤面物件（lastBoard identity）更換時整批重擲（Unity 每次 Trans2BoardData 也重擲）。
   */
  private readonly wolfLevels = new Map<string, number>()
  private wolfLevelsBoard: Board | null = null
  private scaleFactor = 1
  private cx = 0
  private cy = 0
  private cellW = 80
  private cellH = 50
  /** 目前版面尺寸／方向（供 showWildFrame 等非 resize 時機重新定位金木框）。 */
  private viewWidth = 0
  private viewHeight = 0
  private orientation: Orientation = 'landscape'

  private lastBoard: Board | null = null
  private lastTracker: ReelColumn | null = null
  /** 板子震動進度 tween 對象（見 shake）。 */
  private readonly shakeProxy = { t: 0 }
  /** Anticipation 預兆 fallback 光暈（ani_draw 骨骼不可用時）。 */
  private readonly anticipationGlow = new Graphics()
  private readonly glowProxy = { t: 0 }
  /** 聽牌 ani_draw 光柱（依需要建立，reel→Spine）。 */
  private readonly omenSpines = new Map<number, Spine>()
  /** 補牌預兆 ani_reel_bg 底光（reel→Spine）。 */
  private readonly fillOmenSpines = new Map<number, Spine>()
  /** Wild 變身金木框。 */
  private wildFrameSpine: Spine | null = null
  private wildFrameVisible = false
  /**
   * Wild 變身格的顯示覆寫（key=`${reel}:${row}` → 變身前原符號 id）：
   * 補牌先以原符號落下（對照 Unity forceOriginalId），change 動畫結束才顯示 WW。
   * 由 playCollapse 依 step.wildTransforms 設定、playWildTransforms 演完清除。
   */
  private readonly displayOverrides = new Map<string, number>()

  /** 浮動計分板（WinTemp）：單步贏分＝黑色半透明底板＋tx_multiple 位圖字；版面見 WIN_TEMP_AY／WIN_TEMP_BAND_H。 */
  private readonly winTemp = new Container()
  private readonly winTempBg = new Sprite(getUiTexture('winBandBg'))
  private readonly winTempNumber = new BitmapNumber('multiple')
  private readonly winTempProxy = { value: 0 }
  /** WinTemp 的 Unity 全螢幕 Canvas 縮放（與盤面 scaleFactor 不同：直版盤面另乘 0.8）。 */
  private winTempScale = 1

  /** 快停：flag＋喚醒 pending 等待的 resolver。 */
  private forceStopFlag = false
  private forceStopWakers: (() => void)[] = []
  /** 快停熱區啟用中（spin 進行時點盤面任意處＝快停，對應 Unity 全版 quickStopBtn）。 */
  private quickStopActive = false

  constructor(private readonly bus?: EventBus<GameEventMap>) {
    super()

    for (const sprite of [this.stageBgLeft, this.stageBgRight, this.stageFrontLeft, this.stageFrontRight, this.pillar]) {
      sprite.anchor.set(0.5)
    }

    for (let reel = 0; reel < MAIN_REEL_COUNT; reel++) {
      const column: SymbolView[] = []
      for (let row = 0; row < VISIBLE_ROWS; row++) {
        column.push(new SymbolView())
      }
      this.cells.push(column)
    }
    for (let i = 0; i < TRACKER_OVERLAY_REELS.length; i++) {
      this.trackerCells.push(new SymbolView())
    }

    // z-order（底→頂）：底板背景 → 分隔柱 → 預兆光柱（欄位背景光，符號底下）→ 頂輪靜態格 → 主輪靜態格
    //   → 掉落分身 → 前景邊框 → 金框/浮動計分。
    // 聽牌／補牌預兆光柱刻意排在符號之下、且與掉落分身同樣被主盤面遮罩裁切（omenMask），
    // 使它像 symbol 一樣不溢出可視窗、並襯在符號後方而非蓋住符號。
    this.anticipationGlow.visible = false
    this.addChild(this.stageBgLeft, this.stageBgRight, this.pillar)
    this.addChild(this.anticipationGlow, this.omenLayer)
    for (const view of this.trackerCells) this.addChild(view)
    for (const view of this.cells.flat()) this.addChild(view)
    this.addChild(this.fallLayer, this.fallTrackerLayer)
    this.addChild(this.stageFrontLeft, this.stageFrontRight)
    this.addChild(this.overlayLayer)
    this.addChild(this.fallMaskMain, this.fallMaskTracker, this.omenMask)
    this.fallLayer.mask = this.fallMaskMain
    this.fallTrackerLayer.mask = this.fallMaskTracker
    this.omenLayer.mask = this.omenMask

    this.winTemp.visible = false
    this.winTempBg.anchor.set(0.5)
    this.winTempBg.tint = 0x000000
    this.winTempBg.alpha = WIN_TEMP_BG_ALPHA
    this.winTempNumber.setLetterSpacing(WIN_TEMP_SPACING_RATIO)
    this.winTemp.addChild(this.winTempBg, this.winTempNumber)
    this.overlayLayer.addChild(this.winTemp)

    // 快停熱區：spin 進行中點盤面（乃至全畫面）任意處＝快停（對應 Unity 全版 quickStopBtn；
    // 本 view 疊在背景上、底部列/彈窗之下，故不會攔到操作列與彈窗的點擊）。
    this.eventMode = 'static'
    this.on('pointertap', () => {
      if (this.quickStopActive) this.bus?.emit('forceStopRequested', undefined)
    })
    this.hitArea = new Rectangle(0, 0, 0, 0)
  }

  // ── 座標換算（設計座標 → 螢幕座標） ──
  private sx(dx: number): number {
    return this.cx + (dx - DESIGN_CX) * this.scaleFactor
  }
  private sy(dy: number): number {
    return this.cy + (dy - FRAME_CY) * this.scaleFactor
  }
  /** 主輪第 reel 欄中心 x。 */
  private mainColX(reel: number): number {
    return this.sx(MAIN_COL0_X + reel * CELL_W_REF)
  }
  /** 主輪「頂格在 headRow、跨 span 格」的符號中心 y（span=1 時即該格中心）。 */
  private mainSpanCenterY(headRow: number, span: number): number {
    return this.sy(MAIN_ROW0_Y + headRow * CELL_H_REF + ((span - 1) * CELL_H_REF) / 2)
  }
  /** Tracker 頂輪第 i 格中心 x（反序疊加：第 i 格對齊主輪 trackerReelForPosition(i)，index 0→最右 reel4、index 3→最左 reel1；見 constants 說明）。 */
  private trackerCellX(i: number): number {
    return this.mainColX(trackerReelForPosition(i) ?? TRACKER_OVERLAY_REELS[i]!)
  }
  private trackerRowY(): number {
    return this.sy(TRACKER_ROW_Y)
  }

  /** 盤面中央的螢幕座標（子彈飛行找不到來源/目標格時的 fallback 起訖點）。 */
  boardCenter(): { x: number; y: number } {
    return { x: this.sx(DESIGN_CX), y: this.sy(FRAME_CY + 40) }
  }

  /** 指定主輪格（跨距中心）的螢幕座標，供子彈飛行起點（Scatter 收集）。 */
  cellCenter(reel: number, row: number, span = 1): { x: number; y: number } {
    if (reel === TRACKER_REEL_INDEX) {
      const index = Math.max(0, Math.min(TRACKER_OVERLAY_REELS.length - 1, row))
      return { x: this.trackerCellX(index), y: this.trackerRowY() }
    }
    return { x: this.mainColX(reel), y: this.mainSpanCenterY(row, span) }
  }

  /**
   * 板子震動（對應 Unity GamePlayUI.ShakeBoard：DOShakePosition duration=0.2、strength=20、vibrato=50，
   * FG 中套用倍率 ≥10 時的強調）。以衰減正弦水平抖動整個盤面容器，結束歸位。
   */
  async shake(): Promise<void> {
    killTweensOf(this.shakeProxy)
    this.shakeProxy.t = 0
    const amplitude = 20 * this.scaleFactor
    await tweenTo(this.shakeProxy, {
      t: 1,
      duration: 0.2,
      ease: 'none',
      onUpdate: () => {
        const p = this.shakeProxy.t
        const decay = 1 - p
        this.position.set(Math.sin(p * Math.PI * 2 * 10) * amplitude * decay, Math.cos(p * Math.PI * 2 * 8) * amplitude * decay * 0.6)
      },
    })
    if (!this.destroyed) this.position.set(0, 0)
  }

  resize(width: number, height: number, orientation: Orientation): void {
    this.viewWidth = width
    this.viewHeight = height
    this.orientation = orientation
    this.cancelFallAnimation()
    // 若在震動中途 resize，先停止震動並歸位，避免盤面停在偏移位置。
    killTweensOf(this.shakeProxy)
    this.position.set(0, 0)
    this.hitArea = new Rectangle(0, 0, width, height)

    const isPortrait = orientation === 'portrait'
    if (isPortrait) {
      // 直版照 PoriraitUI 覆寫：ReelController scale ×0.8；盤面（Board 節點）中心 aPos (0, 65.3)
      //（1080×1920 畫布、中心原點 y 向上 → 螢幕 y = h/2 − 65.3·s）。
      const sp = unityScalePortrait(width, height)
      this.scaleFactor = sp * 0.8
      this.cx = width / 2
      this.cy = height / 2 - 65.3 * sp
    } else {
      // 橫版照 Unity 全螢幕 Canvas 等比縮放（盤面為 1920×1080 滿版設計的一部分，
      // 設計座標系＝左上原點 y 向下；FRAME_CY 為框中心設計 y）。
      this.scaleFactor = unityScaleLandscape(width, height)
      this.cx = width / 2
      this.cy = height / 2 + (FRAME_CY - 540) * this.scaleFactor
    }
    this.cellW = CELL_W_REF * this.scaleFactor
    this.cellH = CELL_H_REF * this.scaleFactor

    this.layoutFrameHalves(this.stageBgLeft, this.stageBgRight)
    this.layoutFrameHalves(this.stageFrontLeft, this.stageFrontRight)

    this.pillar.width = PILLAR_W * this.scaleFactor
    this.pillar.height = PILLAR_H * this.scaleFactor
    this.pillar.position.set(this.sx(DESIGN_CX), this.sy(PILLAR_Y))

    this.setMaskRect(this.fallMaskMain, MAIN_MASK)
    this.setMaskRect(this.fallMaskTracker, TRACKER_MASK)
    this.setMaskRect(this.omenMask, MAIN_MASK)

    // 浮動計分（WinTemp）：照 Unity 以「畫面座標」定位（見 WIN_TEMP_AY），不隨盤面座標系走。
    const winTempPos = isPortrait
      ? unityAnchorPortrait(0, WIN_TEMP_P_AY, width, height)
      : unityAnchorLandscape(0, WIN_TEMP_AY, width, height)
    this.winTemp.position.set(winTempPos.x, winTempPos.y)
    this.winTempScale = isPortrait ? unityScalePortrait(width, height) : unityScaleLandscape(width, height)
    this.winTempNumber.setHeight(WIN_TEMP_TEXT_H * this.winTempScale)
    this.layoutWinTempBand()

    if (this.wildFrameSpine !== null) this.layoutWildFrame(this.wildFrameSpine)

    this.redrawAtCurrentSize()
  }

  /** 把「以盤面為基準、設計像素座標編排」的整版 Spine 縮放置中到盤面（preWin 等；金木框見 layoutWildFrame）。 */
  private layoutBoardSpine(spine: Spine): void {
    spine.scale.set(this.scaleFactor)
    spine.position.set(this.sx(DESIGN_CX), this.sy(FRAME_CY))
  }

  /**
   * Wild 金木框定位（見 WILD_FRAME_UY_* 說明：骨骼原點非可見中心、直版另有 0.8 縮放，故不走 layoutBoardSpine）。
   * scale 直接沿用 scaleFactor（橫版＝unityScaleLandscape、直版＝unityScalePortrait×0.8，兩者皆與 Unity localScale 吻合），
   * 位置以骨骼原點相對畫面中心的 Unity anchoredPosition 直接換算。
   */
  private layoutWildFrame(spine: Spine): void {
    spine.scale.set(this.scaleFactor)
    const p =
      this.orientation === 'portrait'
        ? unityAnchorPortrait(0, WILD_FRAME_UY_PORTRAIT, this.viewWidth, this.viewHeight)
        : unityAnchorLandscape(0, WILD_FRAME_UY_LANDSCAPE, this.viewWidth, this.viewHeight)
    spine.position.set(p.x, p.y)
  }

  /** 把框架美術的左右兩半縮放定位：左半原樣、右半以 scale.x=-1 鏡像，兩半在中心 x 對接。 */
  private layoutFrameHalves(left: Sprite, right: Sprite): void {
    const w = FRAME_HALF_W * this.scaleFactor
    const h = FRAME_HALF_H * this.scaleFactor
    const y = this.sy(FRAME_CY)
    left.width = w
    left.height = h
    left.position.set(this.cx - FRAME_HALF_OFFSET * this.scaleFactor, y)
    right.width = w
    right.height = h
    right.scale.x = -Math.abs(right.scale.x) // 鏡像（.width 會把 scale.x 設回正值，故重設負號）
    right.position.set(this.cx + FRAME_HALF_OFFSET * this.scaleFactor, y)
  }

  private setMaskRect(graphics: Graphics, rect: { x: number; y: number; w: number; h: number }): void {
    graphics
      .clear()
      .rect(this.sx(rect.x), this.sy(rect.y), rect.w * this.scaleFactor, rect.h * this.scaleFactor)
      .fill(0xffffff)
  }

  private redrawAtCurrentSize(): void {
    if (this.lastBoard !== null) this.renderBoard(this.lastBoard)
    if (this.lastTracker !== null) this.renderTracker(this.lastTracker)
    this.startIdleLoops()
  }

  setStatic(board: Board, trackerColumn: ReelColumn): void {
    this.displayOverrides.clear()
    this.renderBoard(board)
    this.renderTracker(trackerColumn)
    this.startIdleLoops()
  }

  /**
   * 對所有「已落定」的格重掛待機 loop 骨骼（對照 Unity ReelStrip.Initialization 建盤後的 PlayLoopAnimation）。
   * 只有 WW／主遊戲 SS 的骨骼含 loop，其餘符號為 no-op。alpha===1 的守衛排除「等待掉入」的格
   * （playSpin 的 setAllCellsAlpha(0)、playCollapse 的補位格 alpha=0），那些格會在落定時走 playLand→loop。
   * 需在每次 renderBoard/renderTracker（會 dispose 骨骼）之後呼叫。
   */
  private startIdleLoops(): void {
    for (const view of [...this.cells.flat(), ...this.trackerCells]) {
      if (!view.visible || view.alpha !== 1) continue
      view.playIdleLoop()
    }
  }

  /**
   * Turbo 加速開關。錯開等待依 phase 分流（見 exitGroupStaggerMs／fillStaggerMs）：
   * 離場逐欄略過（同 Unity），落定逐欄減半（刻意偏離 Unity 的「同時落定」以保留順序性）。
   * 另 Turbo 也縮短補牌預兆等待（FILL_OMEN_DELAY_TURBO_MS）並取消聽牌/補牌的慢速逐顆（同 Unity）。
   */
  setTurbo(enabled: boolean): void {
    this.turboEnabled = enabled
  }

  /**
   * 設定免費遊戲模式。FG 中：(1) Scatter 符號改用野狼分級圖（scatterLevel 1~3，對照 Unity 依 CellData.Level
   * 選 Lv1/2/3Sprite=S1/S2/S3）；(2) 不做 Scatter 聽牌預兆（對照 Unity ReelController/SlotStateMachine 的
   * !IsFreeGame 守衛，FG 不演 ShowPreWin/Omen/FillOmen）。狀態改變時立即依當前靜態盤面重繪，
   * 讓離開 FG 時野狼還原成一般 SS。由 GameController 於每輪開轉前／FG 結束時呼叫。
   */
  setFreeGame(active: boolean, scatterLevel = 1): void {
    const clampedLevel = Math.max(1, Math.min(SCATTER_WOLF_MAX_LEVEL, Math.floor(scatterLevel)))
    if (this.freeGameActive === active && this.freeGameScatterLevel === clampedLevel) return
    const wasActive = this.freeGameActive
    this.freeGameActive = active
    this.freeGameScatterLevel = clampedLevel
    // 只在「離開 FG」時立即重繪，把野狼還原成一般 SS 供主遊戲。
    // 進入 FG／FG 中升級時「不」重繪：野狼等級套用於下一手停輪建盤（見 lastBoardScatterLevel），
    // 故觸發 FG 的 Scatter 在第一手轉輪落下前仍維持一般 SS（對照 Unity StopSpin_Enter 才寫 CellData.Level）。
    if (wasActive && !active) this.redrawAtCurrentSize()
  }

  /**
   * 統一顯示一顆符號。viewSymbolId 為顯示用原符號 id（角色 Wild 用它選 W2/W3/W4，見 SymbolView）。
   * fgScatterLevel 為此次渲染的野狼「等級上限」（Scatter 專用），預設取當前 FG 等級；轉動清舊符號時
   * 改傳 lastBoardScatterLevel，讓舊符號沿用「顯示當時」的等級（見該欄位說明）。
   * wolfKey 為該格的穩定 key（`${reel}:${row}`／`t:${i}`），實際顯示等級每格獨立隨機 1..min(上限,3)
   * 並在同一份盤面內保持穩定（對照 Unity Trans2BoardData 的 Random.Range(1, min(level+1,4))）。
   */
  private showSymbol(
    view: SymbolView,
    symbolId: number,
    span: number,
    viewSymbolId: number = symbolId,
    fgScatterLevel: number = this.freeGameActive ? this.freeGameScatterLevel : 0,
    wolfKey?: string,
  ): void {
    const scatterLevel =
      symbolId === SCATTER_SYMBOL_ID && fgScatterLevel > 0 ? this.rollWolfLevel(wolfKey, fgScatterLevel) : 0
    view.show(symbolId, span, this.cellW, this.cellH, scatterLevel, viewSymbolId)
  }

  /** 取（或擲出）指定格的野狼臉等級：1..min(tier, SCATTER_WOLF_MAX_LEVEL)，同一盤面內同 key 穩定。 */
  private rollWolfLevel(wolfKey: string | undefined, tier: number): number {
    const bound = Math.max(1, Math.min(SCATTER_WOLF_MAX_LEVEL, Math.floor(tier)))
    if (wolfKey === undefined) return 1 + Math.floor(Math.random() * bound)
    if (this.wolfLevelsBoard !== this.lastBoard) {
      this.wolfLevels.clear()
      this.wolfLevelsBoard = this.lastBoard
    }
    let level = this.wolfLevels.get(wolfKey)
    if (level === undefined || level > bound) {
      level = 1 + Math.floor(Math.random() * bound)
      this.wolfLevels.set(wolfKey, level)
    }
    return level
  }

  /** Wild 變身格的顯示覆寫查詢：有覆寫時回原符號 id（變身前），否則回盤面符號。 */
  private displaySymbolFor(reel: number, row: number, symbol: number): number {
    return this.displayOverrides.get(`${reel}:${row}`) ?? symbol
  }

  /** 依盤面資料更新主輪靜態格：只繪製「繪製根」（頂格/一般格），被覆蓋的延續格隱藏。 */
  private renderBoard(board: Board): void {
    this.lastBoard = board
    this.lastBoardScatterLevel = this.freeGameActive ? this.freeGameScatterLevel : 0
    for (let reel = 0; reel < MAIN_REEL_COUNT; reel++) {
      const column = board[reel]
      for (let row = 0; row < VISIBLE_ROWS; row++) {
        const view = this.cells[reel]![row]!
        const cell = column?.[row]
        if (cell === undefined || isCovered(cell)) {
          view.visible = false
          continue
        }
        const span = cellSpan(cell)
        view.visible = true
        view.alpha = 1
        view.scale.set(1)
        const displaySym = this.displaySymbolFor(reel, row, cell.symbol)
        // 變身覆寫生效時顯示原符號（一般造型）；否則用該格 viewSymbolId（角色 Wild → W2/W3/W4 造型）。
        const viewSym = displaySym === cell.symbol ? cellViewSymbol(cell) : displaySym
        this.showSymbol(view, displaySym, span, viewSym, undefined, `${reel}:${row}`)
        view.position.set(this.mainColX(reel), this.mainSpanCenterY(row, span))
      }
    }
  }

  private renderTracker(trackerColumn: ReelColumn): void {
    this.lastTracker = trackerColumn
    for (let i = 0; i < TRACKER_OVERLAY_REELS.length; i++) {
      const view = this.trackerCells[i]!
      const cell = trackerColumn[i]
      if (cell === undefined) {
        view.visible = false
        continue
      }
      view.visible = true
      view.alpha = 1
      view.scale.set(1)
      this.showSymbol(view, cell.symbol, 1, cellViewSymbol(cell), undefined, `t:${i}`)
      view.position.set(this.trackerCellX(i), this.trackerRowY())
    }
  }

  private cancelFallAnimation(): void {
    this.hideAnticipation()
    this.hideFillOmen()
    this.forceStop() // 喚醒所有 pending 等待，避免 resize 後流程卡住
    const clones = [...this.fallLayer.children, ...this.fallTrackerLayer.children]
    if (clones.length === 0) return
    killTweensOf(clones)
    for (const clone of clones) clone.destroy()
    this.fallLayer.removeChildren()
    this.fallTrackerLayer.removeChildren()
    this.setAllCellsAlpha(1)
  }

  /** 一次設定所有主輪＋Tracker 頂輪靜態格的透明度（掉落動畫全程隱藏靜態格、僅以分身呈現）。 */
  private setAllCellsAlpha(alpha: number): void {
    for (const view of this.cells.flat()) view.alpha = alpha
    for (const view of this.trackerCells) view.alpha = alpha
  }

  // ── 快停 ──

  /** 玩家快停：略過所有逐顆/逐欄/預兆等待（進行中的掉落 tween 照播）。對應 Unity ForceStopRotation。 */
  forceStop(): void {
    this.forceStopFlag = true
    const wakers = this.forceStopWakers
    this.forceStopWakers = []
    for (const wake of wakers) wake()
  }

  private resetForceStop(): void {
    this.forceStopFlag = false
  }

  /** 等待 ms（快停時立即返回；快停旗標已立則完全不等）。 */
  private waitOrForce(ms: number): Promise<void> {
    if (this.forceStopFlag || ms <= 0) return Promise.resolve()
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.forceStopWakers = this.forceStopWakers.filter((w) => w !== wake)
        resolve()
      }, ms)
      const wake = (): void => {
        clearTimeout(timer)
        resolve()
      }
      this.forceStopWakers.push(wake)
    })
  }

  /** spin 進行中啟用「點盤面任意處＝快停」熱區。 */
  setQuickStopActive(active: boolean): void {
    this.quickStopActive = active
    this.cursor = active ? 'pointer' : 'default'
  }

  // ── 轉動揭示 ──

  /**
   * 播放一次完整轉動揭示（見檔頭 ①②③）。每一主輪整欄落定時呼叫 onReelStopped(reel)。
   * suppressOmen：本手已播 ShowPreWin 預告時不再聽牌（對應 Unity IsShowPreWin 守衛）。
   */
  async playSpin(board: Board, trackerColumn: ReelColumn, onReelStopped?: (reel: number) => void, suppressOmen = false): Promise<void> {
    this.cancelFallAnimation()
    this.resetForceStop()
    this.hideWildFrame()
    this.displayOverrides.clear()
    this.bus?.emit('reelSpinStarted', undefined)

    // ① 舊符號離場（首個盤面沒有舊符號可清）。
    await this.exitOldSymbols()

    // ② 換上結果盤面（隱藏）＋保底空窗（Turbo/快停也不縮短，對應 Unity _spinDuration 恆等待）。
    this.renderBoard(board)
    this.renderTracker(trackerColumn)
    this.setAllCellsAlpha(0)
    await delay(REEL_SPIN_DURATION_S * 1000)

    // 聽牌 Omen（僅主遊戲、未播預告時）：落地 2 顆 Scatter 起聽、達 3 顆止。
    const omenReels = this.freeGameActive || suppressOmen ? new Set<number>() : this.computeOmenReels(board)
    if (omenReels.size > 0) this.bus?.emit('anticipationStarted', undefined)

    // ③ 結果符號自上方掉入：逐欄 0.1s 錯開（Turbo/快停不錯開）、Tracker 與第 0 欄同組。
    const scatterCounter = { count: 0 }
    const tasks: Promise<void>[] = []
    for (let reel = 0; reel < board.length; reel++) {
      tasks.push(
        this.fillReelColumn(reel, board[reel]!, {
          groupDelayMs: this.fillStaggerMs(reel * REEL_STOP_INTERVAL_S * 1000),
          omen: omenReels.has(reel),
          scatterCounter,
          revealOnly: null,
          staggerCells: false,
        }).then(() => {
          this.revealReelStatic(reel)
          this.bus?.emit('reelColumnLanded', { reel })
          onReelStopped?.(reel)
        }),
      )
    }
    tasks.push(
      this.fillTracker(trackerColumn, { groupDelayMs: 0, revealOnly: null, staggerCells: false }).then(() => {
        this.bus?.emit('reelColumnLanded', { reel: TRACKER_REEL_INDEX })
      }),
    )
    await Promise.all(tasks)
    if (omenReels.size > 0) this.bus?.emit('anticipationEnded', undefined)
    this.hideAnticipation()
  }

  /**
   * 離場「逐欄」錯開。Turbo 略過：Unity 的 Rotate_Enter 以 `StartRotation(!isTurbo)` 傳入 useDelay，
   * ReelController.cs:84 的 `if (useDelay) await Delay(_startInterval)` 因此完全不等。
   */
  private exitGroupStaggerMs(ms: number): number {
    return this.turboEnabled || this.forceStopFlag ? 0 : ms
  }

  /**
   * 落定「逐欄／逐顆」錯開（轉動揭示 playSpin 與消除補位 playFill 共用同一批 fill 原語）。
   * [刻意偏離 Unity] Unity 的 Turbo 借用快停路徑（SlotStateMachine.cs:227 進 StopSpin 前先 ForceStopRotation）
   * 使逐欄（ReelController.cs:107）與逐顆（ReelStrip.cs:226）雙雙歸零＝7 條輪同時落定；本移植保留左→右的
   * 順序性，取一般值的一半。快停仍歸零。理由與係數見 constants.TURBO_STAGGER_SCALE。
   */
  private fillStaggerMs(ms: number): number {
    if (this.forceStopFlag) return 0
    return this.turboEnabled ? ms * TURBO_STAGGER_SCALE : ms
  }

  /** ① 舊符號整欄墜落離場：逐欄 0.1s 錯開、欄內底列先掉、逐顆 0.05s；Tracker 向左滑出（與第 0 欄同組）。 */
  private async exitOldSymbols(): Promise<void> {
    const board = this.lastBoard
    const tracker = this.lastTracker
    const tasks: Promise<void>[] = []

    if (board !== null) {
      for (let reel = 0; reel < board.length; reel++) {
        const column = board[reel]!
        const roots: { row: number; span: number; symbol: number; viewSymbolId: number }[] = []
        this.forEachDrawRoot(column, (row, span, symbol, viewSymbolId) => roots.push({ row, span, symbol, viewSymbolId }))
        // 底列先離場（bottomRow 大者先），一顆接一顆。
        roots.sort((a, b) => b.row + b.span - (a.row + a.span))
        const x = this.mainColX(reel)
        const groupDelay = this.exitGroupStaggerMs(reel * REEL_START_INTERVAL_S * 1000)
        tasks.push(
          (async () => {
            await this.waitOrForce(groupDelay)
            const exits: Promise<void>[] = []
            for (const root of roots) {
              const view = this.cells[reel]?.[root.row]
              if (view !== undefined) view.alpha = 0
              const clone = new SymbolView()
              // 用「顯示當時」的野狼等級（lastBoardScatterLevel）而非最新等級：觸發 FG 的 Scatter 第一手仍以一般 SS 離場。
              this.showSymbol(clone, root.symbol, root.span, root.viewSymbolId, this.lastBoardScatterLevel, `${reel}:${root.row}`)
              clone.position.set(x, this.mainSpanCenterY(root.row, root.span))
              this.fallLayer.addChild(clone)
              const dy = (VISIBLE_ROWS + root.span) * this.cellH
              exits.push(
                tweenTo(clone, { y: clone.y + dy, duration: REEL_EXIT_DURATION_S, ease: 'power2.in' }).then(() => {
                  if (!clone.destroyed) {
                    this.fallLayer.removeChild(clone)
                    clone.destroy()
                  }
                }),
              )
              // 整欄同時離場（使用者要求）：不再逐顆錯開，欄內所有符號一起掉出、欄間仍由 groupDelay 錯開。
            }
            await Promise.all(exits)
          })(),
        )
      }
    }

    if (tracker !== null) {
      const cols = TRACKER_OVERLAY_REELS.length
      const y = this.trackerRowY()
      tasks.push(
        (async () => {
          const exits: Promise<void>[] = []
          // 最左格先滑出（對稱主輪「底列先離場」；index 大＝畫面左＝離右方入口最遠）。
          for (let i = cols - 1; i >= 0; i--) {
            const cell = tracker[i]
            const view = this.trackerCells[i]
            if (view !== undefined) view.alpha = 0
            if (cell === undefined) continue
            const clone = new SymbolView()
            this.showSymbol(clone, cell.symbol, 1, cellViewSymbol(cell), this.lastBoardScatterLevel, `t:${i}`)
            clone.position.set(this.trackerCellX(i), y)
            this.fallTrackerLayer.addChild(clone)
            const dx = (cols + 1) * this.cellW
            exits.push(
              tweenTo(clone, { x: clone.x - dx, duration: REEL_EXIT_DURATION_S, ease: 'power2.in' }).then(() => {
                if (!clone.destroyed) {
                  this.fallTrackerLayer.removeChild(clone)
                  clone.destroy()
                }
              }),
            )
            // 整排同時離場（使用者要求）：不再逐格錯開。
          }
          await Promise.all(exits)
        })(),
      )
    }

    if (tasks.length > 0) await Promise.all(tasks)
  }

  /**
   * 單欄補入掉落。revealOnly 為 null 時整欄所有繪製根都掉入（轉動揭示）；為集合時只掉入指定頂格列
   * （消除補位：只補 fromRow===null 的新符號）。落定順序：底列先、往上堆疊；逐顆間隔 0.05s
   * （聽牌/補牌預兆欄改 0.4s 慢速）；每顆落定走三段彈跳並發 Scatter 落地事件。
   */
  private async fillReelColumn(
    reel: number,
    finalColumn: ReelColumn,
    opts: {
      groupDelayMs: number
      omen: boolean
      scatterCounter: { count: number }
      /** null＝整欄；集合＝只補這些頂格列。 */
      revealOnly: ReadonlySet<number> | null
      // 同一欄是否「逐顆」錯開落定：轉動揭示（playSpin）傳 false ⇒ 整欄同時掉、無逐顆間隔（使用者要求）；
      // 消除補位（playFill）傳 true ⇒ 維持逐顆 0.05s。聽牌欄（omen）不受此參數影響、恆逐顆慢速。
      staggerCells: boolean
    },
  ): Promise<void> {
    const roots: { row: number; span: number; symbol: number; viewSymbolId: number }[] = []
    this.forEachDrawRoot(finalColumn, (row, span, symbol, viewSymbolId) => {
      if (opts.revealOnly !== null && !opts.revealOnly.has(row)) return
      // Wild 變身格：補牌以「變身前原符號」落下（對照 Unity forceOriginalId），變身演出後才換角色 Wild 造型。
      const displaySym = this.displaySymbolFor(reel, row, symbol)
      const viewSym = displaySym === symbol ? viewSymbolId : displaySym
      roots.push({ row, span, symbol: displaySym, viewSymbolId: viewSym })
    })
    if (roots.length === 0) return
    // 底列先落、往上堆疊。
    roots.sort((a, b) => b.row + b.span - (a.row + a.span))

    await this.waitOrForce(opts.groupDelayMs)
    if (opts.omen) this.showOmenSpine(reel)

    const x = this.mainColX(reel)
    const drops: Promise<void>[] = []
    for (const root of roots) {
      const destY = this.mainSpanCenterY(root.row, root.span)
      const clone = new SymbolView()
      this.showSymbol(clone, root.symbol, root.span, root.viewSymbolId, undefined, `${reel}:${root.row}`)
      clone.position.set(x, this.mainSpanCenterY(root.row - VISIBLE_ROWS - 1, root.span))
      this.fallLayer.addChild(clone)
      drops.push(
        this.dropCellTween(clone, 'y', destY).then(() => {
          const view = this.cells[reel]?.[root.row]
          if (view !== undefined) view.alpha = 1
          if (root.symbol === SCATTER_SYMBOL_ID) {
            opts.scatterCounter.count += 1
            this.bus?.emit('scatterLanded', { ordinal: this.freeGameActive ? 3 : opts.scatterCounter.count })
          }
          // 落定演出（對照 Unity DoFillEffect 尾端的 `_playEffectHandle(symbolView, "land", 1.0f, false)`，
          // 順序同樣是落地音效之後）。只有 WW／H1／主遊戲 SS 有 land 骨骼，其餘符號此呼叫為 no-op。
          view?.playLand()
          if (!clone.destroyed) {
            this.fallLayer.removeChild(clone)
            clone.destroy()
          }
        }),
      )
      // 逐顆間隔（決定下一顆是否等本顆先掉）：
      // - 聽牌欄（omen）：非 Turbo 時 0.4s 慢速掉（anticipation 緊張效果，恆逐顆）、Turbo 退回 0.05s。
      // - 非 omen 欄：staggerCells 為 true 才逐顆 0.05s；轉動揭示傳 false ⇒ 整欄同時掉、無間隔（使用者要求）。
      let interval: number | null = null
      if (opts.omen) interval = this.turboEnabled ? FILL_INTERVAL_S : FILL_DURATION_S
      else if (opts.staggerCells) interval = FILL_INTERVAL_S
      if (interval !== null) await this.waitOrForce(this.fillStaggerMs(interval * 1000))
    }
    await Promise.all(drops)
    if (opts.omen) this.hideOmenSpine(reel)
  }

  /** Tracker 頂輪補入：由右往左滑入（與主輪掉落同曲線，水平版）。revealOnly 同 fillReelColumn。 */
  private async fillTracker(
    finalTracker: ReelColumn,
    opts: { groupDelayMs: number; revealOnly: ReadonlySet<number> | null; staggerCells: boolean },
  ): Promise<void> {
    const cols = TRACKER_OVERLAY_REELS.length
    const y = this.trackerRowY()
    const indices: number[] = []
    for (let i = 0; i < cols; i++) {
      if (opts.revealOnly !== null && !opts.revealOnly.has(i)) continue
      if (finalTracker[i] === undefined) continue
      indices.push(i)
    }
    if (indices.length === 0) return
    // 最左格先落定、往右依序堆疊（對稱主輪「底列先落」；離右方入口最遠者先就位，
    // 反序疊加下 index 大＝畫面左＝這條輪的底部，故降序）。
    indices.sort((a, b) => b - a)

    await this.waitOrForce(opts.groupDelayMs)
    const dist = (cols + 1) * this.cellW
    const drops: Promise<void>[] = []
    for (const i of indices) {
      const destX = this.trackerCellX(i)
      const clone = new SymbolView()
      this.showSymbol(clone, finalTracker[i]!.symbol, 1, cellViewSymbol(finalTracker[i]!), undefined, `t:${i}`)
      clone.position.set(destX + dist, y)
      this.fallTrackerLayer.addChild(clone)
      drops.push(
        this.dropCellTween(clone, 'x', destX).then(() => {
          const view = this.trackerCells[i]
          if (view !== undefined) view.alpha = 1
          // 落定演出，同 fillReelColumn（Unity 頂輪走同一支 ReelStrip.DoFillEffect）。
          view?.playLand()
          if (!clone.destroyed) {
            this.fallTrackerLayer.removeChild(clone)
            clone.destroy()
          }
        }),
      )
      // 逐格錯開：轉動揭示傳 false ⇒ 整排同時滑入；消除補位傳 true ⇒ 維持逐格 0.05s。
      if (opts.staggerCells) await this.waitOrForce(this.fillStaggerMs(FILL_INTERVAL_S * 1000))
    }
    await Promise.all(drops)
  }

  /**
   * 單顆補入的三段掉落（對照 Unity DoFillEffect）：
   * 落到定位（0.8×0.4s InQuad）→ 逆行進方向彈起 20px（0.1×0.4s OutQuad）→ 落定（0.1×0.4s InQuad）。
   */
  private async dropCellTween(clone: SymbolView, axis: 'x' | 'y', dest: number): Promise<void> {
    const bounce = FILL_BOUNCE_DESIGN_PX * this.scaleFactor
    // 主輪向下掉 → 彈起方向向上（-y）；Tracker 向左滑 → 彈回向右（+x）。
    const bounceTo = axis === 'y' ? dest - bounce : dest + bounce
    await tweenTo(clone, { [axis]: dest, duration: FILL_DURATION_S * FILL_SEG_DROP, ease: 'power2.in' })
    if (clone.destroyed) return
    await tweenTo(clone, { [axis]: bounceTo, duration: FILL_DURATION_S * FILL_SEG_BOUNCE, ease: 'power2.out' })
    if (clone.destroyed) return
    await tweenTo(clone, { [axis]: dest, duration: FILL_DURATION_S * FILL_SEG_BOUNCE, ease: 'power2.in' })
  }

  /** 對一欄的每個繪製根（頂格/一般格，跳過被覆蓋的延續格）呼叫 fn(頂格列, 跨距, 符號, 顯示用符號)。 */
  private forEachDrawRoot(
    column: ReelColumn,
    fn: (rowIndex: number, span: number, symbol: number, viewSymbolId: number) => void,
  ): void {
    for (let row = 0; row < column.length; row++) {
      const cell = column[row]
      if (cell === undefined || isCovered(cell)) continue
      fn(row, cellSpan(cell), cell.symbol, cellViewSymbol(cell))
    }
  }

  /** 顯示某主輪的靜態格（透明度設回 1）；被覆蓋的延續格已由 renderBoard 設為 visible=false。 */
  private revealReelStatic(reel: number): void {
    const column = this.cells[reel]
    if (column === undefined) return
    for (const view of column) view.alpha = 1
  }

  // ── 聽牌 Omen / 補牌預兆 ──

  /**
   * 聽牌欄集合：由左往右累加各輪落地的 Scatter 數，某輪起聽的條件是「其左側累計 ≥ ANTICIPATION_SCATTER_COUNT
   * 且 < OMEN_MAX_SCATTER」（達 3 顆後不再聽，對照 Unity `_omenSymbolCount < 3` 守衛）。
   */
  private computeOmenReels(board: Board): Set<number> {
    const omen = new Set<number>()
    let cumulative = 0
    for (let reel = 0; reel < board.length; reel++) {
      if (cumulative >= ANTICIPATION_SCATTER_COUNT && cumulative < OMEN_MAX_SCATTER) omen.add(reel)
      cumulative += board[reel]!.filter((cell) => !isCovered(cell) && cell.symbol === SCATTER_SYMBOL_ID).length
    }
    return omen
  }

  /** 在指定欄顯示聽牌光柱（ani_draw "draw_down" loop）；骨骼不可用時退回金色脈衝光暈。 */
  private showOmenSpine(reel: number): void {
    let spine = this.omenSpines.get(reel) ?? null
    if (spine === null) {
      spine = createEffectSpine('draw')
      if (spine !== null) {
        this.omenSpines.set(reel, spine)
        this.omenLayer.addChild(spine)
      }
    }
    if (spine !== null) {
      if (hasAnimation(spine, EFFECT_ANIM.omenDraw)) spine.state.setAnimation(0, EFFECT_ANIM.omenDraw, true)
      this.fitColumnSpine(spine, reel)
      spine.visible = true
      return
    }
    this.showAnticipationGlow(reel, reel)
  }

  private hideOmenSpine(reel: number): void {
    const spine = this.omenSpines.get(reel)
    if (spine !== undefined) spine.visible = false
  }

  /** 把欄位特效骨骼縮放定位到指定主輪欄（骨骼以設計像素編排單欄大小，origin 置欄中心）。 */
  private fitColumnSpine(spine: Spine, reel: number): void {
    spine.scale.set(this.scaleFactor)
    spine.position.set(this.mainColX(reel), this.sy(MAIN_ROW0_Y + ((VISIBLE_ROWS - 1) * CELL_H_REF) / 2))
  }

  /** 顯示補牌預兆底光（ani_reel_bg "reel_bg" loop）於指定欄集合。 */
  private showFillOmen(reels: ReadonlySet<number>): void {
    for (const reel of reels) {
      let spine = this.fillOmenSpines.get(reel) ?? null
      if (spine === null) {
        spine = createEffectSpine('reelBg')
        if (spine !== null) {
          this.fillOmenSpines.set(reel, spine)
          this.omenLayer.addChild(spine)
        }
      }
      if (spine !== null) {
        if (hasAnimation(spine, EFFECT_ANIM.fillOmen)) spine.state.setAnimation(0, EFFECT_ANIM.fillOmen, true)
        this.fitColumnSpine(spine, reel)
        spine.visible = true
      }
    }
  }

  private hideFillOmen(): void {
    for (const spine of this.fillOmenSpines.values()) spine.visible = false
  }

  /** fallback：在預兆欄覆上金色脈衝光暈（ani_draw 不可用時）。 */
  private showAnticipationGlow(fromReel: number, toReel: number): void {
    const leftX = this.mainColX(fromReel) - this.cellW / 2
    const rightX = this.mainColX(toReel) + this.cellW / 2
    const width = rightX - leftX
    const topY = this.mainSpanCenterY(0, 1) - this.cellH / 2
    const height = VISIBLE_ROWS * this.cellH
    const glow = this.anticipationGlow
    glow
      .clear()
      .roundRect(leftX, topY, width, height, this.cellW * 0.08)
      .fill({ color: 0xffe45c, alpha: 0.22 })
      .roundRect(leftX, topY, width, height, this.cellW * 0.08)
      .stroke({ color: 0xfff0b0, width: Math.max(2, this.cellW * 0.03), alpha: 0.9 })
    glow.visible = true
    killTweensOf(this.glowProxy)
    this.glowProxy.t = 0
    void tweenTo(this.glowProxy, {
      t: 1,
      duration: 0.5,
      ease: 'sine.inOut',
      repeat: -1,
      yoyo: true,
      onUpdate: () => {
        glow.alpha = 0.55 + this.glowProxy.t * 0.45
      },
    })
  }

  private hideAnticipation(): void {
    killTweensOf(this.glowProxy)
    this.anticipationGlow.visible = false
    this.anticipationGlow.alpha = 1
    for (const spine of this.omenSpines.values()) spine.visible = false
  }

  // ── 開轉「會中免費遊戲」預告（ShowPreWin） ──

  /** 播放 ani_preWin 預告特效（全長），蓋在盤面中央。骨骼不可用時以金色閃光近似。 */
  async playPreWin(): Promise<void> {
    const spine = createEffectSpine('preWin')
    if (spine !== null && hasAnimation(spine, EFFECT_ANIM.preWin)) {
      this.overlayLayer.addChild(spine)
      this.layoutBoardSpine(spine)
      spine.state.setAnimation(0, EFFECT_ANIM.preWin, false)
      await delay(animationDuration(spine, EFFECT_ANIM.preWin, 1.2) * 1000)
      if (!spine.destroyed) {
        this.overlayLayer.removeChild(spine)
        spine.destroy()
      }
      return
    }
    if (spine !== null && !spine.destroyed) spine.destroy()
    // fallback：金色光暈快閃兩下。
    this.showAnticipationGlow(0, MAIN_REEL_COUNT - 1)
    await delay(900)
    this.hideAnticipation()
  }

  // ── 浮動計分（WinTemp） ──

  /**
   * 依目前數字寬度重排底板：Unity 的 HorizontalLayoutGroup(padding 80/80)＋ContentSizeFitter(Preferred)
   * 等價於「寬＝160＋數字寬」，高固定 115（VerticalFit 為 Unconstrained）。
   */
  private layoutWinTempBand(): void {
    this.winTempBg.width = WIN_TEMP_PAD_X * this.winTempScale + this.winTempNumber.contentWidth
    this.winTempBg.height = WIN_TEMP_BAND_H * this.winTempScale
  }

  /** 顯示/更新浮動計分：從目前值跑分到 amount（0.3s Linear，對應 Unity WinTemp CountUp）。 */
  showWinTemp(amount: number): void {
    if (!this.winTemp.visible) {
      this.winTemp.visible = true
      this.winTempProxy.value = 0
      this.winTempNumber.setText(formatMoney(0))
      this.layoutWinTempBand()
    }
    killTweensOf(this.winTempProxy)
    void tweenTo(this.winTempProxy, {
      value: amount,
      duration: WIN_TEMP_COUNT_S,
      ease: 'none',
      onUpdate: () => {
        // 跑分中位數會變（如 9.99→10.00），底板寬跟著重算（對應 Unity 每次改字都會重跑 layout）。
        this.winTempNumber.setText(formatMoney(this.winTempProxy.value))
        this.layoutWinTempBand()
      },
    })
  }

  /** 隱藏浮動計分（對應 HideWinTempEvent）。 */
  hideWinTemp(): void {
    killTweensOf(this.winTempProxy)
    this.winTempProxy.value = 0
    this.winTemp.visible = false
  }

  // ── 消除連鎖原語（由 GameController 依 Unity DestroySymbol/FillSymbol/TransformSymbol 時序驅動） ──

  /** 本步驟中獎格（主輪以繪製根為單位）與 Tracker 中獎位置。 */
  private collectWinningViews(step: TumbleStep): { main: { view: SymbolView; reel: number; row: number }[]; tracker: SymbolView[] } {
    const winningRowsByReel = new Map<number, Set<number>>()
    const winningTrackerPositions = new Set<number>()
    for (const cell of step.wins.flatMap((win) => win.cells)) {
      if (cell.reel === TRACKER_REEL_INDEX) {
        winningTrackerPositions.add(cell.row)
        continue
      }
      const rows = winningRowsByReel.get(cell.reel) ?? new Set<number>()
      rows.add(cell.row)
      winningRowsByReel.set(cell.reel, rows)
    }

    const main: { view: SymbolView; reel: number; row: number }[] = []
    for (let reel = 0; reel < MAIN_REEL_COUNT; reel++) {
      const winningRows = winningRowsByReel.get(reel)
      if (winningRows === undefined) continue
      const column = step.boardBefore[reel]!
      for (let row = 0; row < column.length; row++) {
        const cell = column[row]!
        if (isCovered(cell)) continue
        const span = cellSpan(cell)
        // 長條符號只要其涵蓋的任一列中獎，整顆視為中獎（頂格 view 播 win/remove）。
        let winning = false
        for (let r = row; r < row + span; r++) {
          if (winningRows.has(r)) {
            winning = true
            break
          }
        }
        if (!winning) continue
        const view = this.cells[reel]?.[row]
        if (view !== undefined) main.push({ view, reel, row })
      }
    }
    const tracker: SymbolView[] = []
    for (const position of winningTrackerPositions) {
      const view = this.trackerCells[position]
      if (view !== undefined) tracker.push(view)
    }
    return { main, tracker }
  }

  /** 非中獎格變暗（0.502 灰、0.1s），對應 Unity DestroySymbol 的 SetMask(true)。 */
  maskLosers(step: TumbleStep): void {
    const winning = this.collectWinningViews(step)
    const winningSet = new Set<SymbolView>([...winning.main.map((w) => w.view), ...winning.tracker])
    for (const view of [...this.cells.flat(), ...this.trackerCells]) {
      if (!view.visible) continue
      view.setDimmed(!winningSet.has(view))
    }
  }

  /** 還原全部變暗（0.1s），對應 SetMask(false)。 */
  unmaskLosers(): void {
    for (const view of [...this.cells.flat(), ...this.trackerCells]) {
      if (!view.visible) continue
      view.setDimmed(false)
    }
  }

  /**
   * 中獎演出：全部中獎格同時播 win loop（1.5s）→ remove 爆散（對應 Unity WhenAll(PlayWinAnimation(true))）。
   * Wild 替代 H2/H3/H4 連線時先換 W2/W3/W4 造型。
   */
  async playWinAnimations(step: TumbleStep): Promise<void> {
    const winning = this.collectWinningViews(step)
    // 角色 Wild 的 W2/W3/W4 造型由盤面 viewSymbolId 持久呈現（見 SymbolView.applySprite / playWinLoop），
    // 此處不再依「中的是哪條線」逐格覆寫；原生 WW 連線時維持 WW（對照 Unity：只有 ViewSymbolId≠SymbolId
    // 的角色 Wild 才顯示角色造型，原生 WW 恆為 WW）。
    const tasks: Promise<void>[] = []
    for (const { view } of winning.main) tasks.push(view.playEliminate())
    for (const view of winning.tracker) tasks.push(view.playEliminate())
    if (tasks.length > 0) await Promise.all(tasks)
  }

  /**
   * 進免費遊戲前的 Scatter 中獎展示（對應 Unity ToFg：非 Scatter 全變暗、Scatter 播 win loop 2s 不消除）。
   * cells 為最終盤面上的 Scatter 位置（含 Tracker）。
   */
  async playScatterShowcase(cells: readonly CellRef[]): Promise<void> {
    const scatterViews: SymbolView[] = []
    for (const ref of cells) {
      const view = ref.reel === TRACKER_REEL_INDEX ? this.trackerCells[ref.row] : this.cells[ref.reel]?.[ref.row]
      if (view !== undefined && view.visible) scatterViews.push(view)
    }
    const scatterSet = new Set(scatterViews)
    for (const view of [...this.cells.flat(), ...this.trackerCells]) {
      if (!view.visible) continue
      view.setDimmed(!scatterSet.has(view))
    }
    await Promise.all(scatterViews.map((view) => view.playWinLoop(WIN_LOOP_HOLD_MS, true)))
    this.unmaskLosers()
  }

  /** FG Scatter 收集：單顆 Scatter 播 win loop（2s、不消除），對應 Unity 收集迴圈的 PlayWinAnimation(pos,false)。 */
  async playScatterCollectHighlight(ref: CellRef): Promise<void> {
    const view = ref.reel === TRACKER_REEL_INDEX ? this.trackerCells[ref.row] : this.cells[ref.reel]?.[ref.row]
    if (view === undefined || !view.visible) return
    await view.playWinLoop(WIN_LOOP_HOLD_MS, true)
  }

  /**
   * 消除後的下墜補洞（對應 Unity DropSymbols）：把盤面換成 boardAfter，存活且位移的符號全部「同時」
   * 以 0.4s OutBounce 掉到新位置（主輪往下、Tracker 頂輪往畫面左，同一條輪的水平版）；
   * 全新補位符號維持隱藏，交由 playFill 掉入。
   */
  async playCollapse(step: TumbleStep): Promise<void> {
    // 設定本步驟的 Wild 變身顯示覆寫：boardAfter 上的變身格先顯示原符號，change 演完（playWildTransforms）才換 WW。
    this.displayOverrides.clear()
    for (const t of step.wildTransforms) this.displayOverrides.set(`${t.ref.reel}:${t.ref.row}`, t.fromSymbol)
    this.renderBoard(step.boardAfter)
    this.renderTracker(step.trackerAfter)

    const tweens: Promise<void>[] = []
    step.boardAfter.forEach((column, reel) => {
      const fall = step.fallMap[reel] ?? []
      const x = this.mainColX(reel)
      for (let finalRow = 0; finalRow < column.length; finalRow++) {
        const cell = column[finalRow]!
        if (isCovered(cell)) continue
        const span = cellSpan(cell)
        const entry = fall.find((e) => e.finalRow === finalRow)
        const fromRow = entry?.fromRow ?? finalRow
        const target = this.cells[reel]?.[finalRow]
        if (target === undefined) continue

        if (entry !== undefined && entry.fromRow === null) {
          // 全新補位符號：留給 playFill 掉入。
          target.alpha = 0
          continue
        }
        if (fromRow === finalRow) continue // 未位移，維持原樣

        target.alpha = 0
        const srcCell = step.boardBefore[reel]?.[fromRow]
        const symbolId = srcCell?.symbol ?? cell.symbol
        // 存活的角色 Wild 下墜時保留其 W2/W3/W4 造型（viewSymbolId 由來源格帶入）。
        const viewSym = srcCell !== undefined ? cellViewSymbol(srcCell) : cellViewSymbol(cell)
        const clone = new SymbolView()
        this.showSymbol(clone, symbolId, span, viewSym, undefined, `${reel}:${finalRow}`)
        clone.position.set(x, this.mainSpanCenterY(fromRow, span))
        this.fallLayer.addChild(clone)
        tweens.push(
          tweenTo(clone, { y: this.mainSpanCenterY(finalRow, span), duration: DROP_DURATION_S, ease: 'bounce.out' }).then(() => {
            target.alpha = 1
            if (!clone.destroyed) {
              this.fallLayer.removeChild(clone)
              clone.destroy()
            }
          }),
        )
      }
    })
    // Tracker 頂輪：倖存符號往畫面左側遞補（水平版下墜），新補位格先隱藏留給 playFill 由右滑入。
    const trackerY = this.trackerRowY()
    for (const entry of step.trackerFall) {
      const target = this.trackerCells[entry.finalRow]
      if (target === undefined) continue
      if (entry.fromRow === null) {
        target.alpha = 0
        continue
      }
      if (entry.fromRow === entry.finalRow) continue // 未位移，維持原樣
      const srcCell = step.trackerBefore[entry.fromRow]
      if (srcCell === undefined) continue

      target.alpha = 0
      const clone = new SymbolView()
      this.showSymbol(clone, srcCell.symbol, 1, cellViewSymbol(srcCell), undefined, `t:${entry.finalRow}`)
      clone.position.set(this.trackerCellX(entry.fromRow), trackerY)
      this.fallTrackerLayer.addChild(clone)
      tweens.push(
        tweenTo(clone, { x: this.trackerCellX(entry.finalRow), duration: DROP_DURATION_S, ease: 'bounce.out' }).then(() => {
          target.alpha = 1
          if (!clone.destroyed) {
            this.fallTrackerLayer.removeChild(clone)
            clone.destroy()
          }
        }),
      )
    }

    if (tweens.length > 0) {
      await Promise.all(tweens)
      this.bus?.emit('survivorsDropped', undefined)
    }
    // 倖存符號在上方 renderBoard 重繪時失去待機骨骼（show 會 dispose），落定後重掛；
    // 等待補入的格（alpha=0）不受影響，會在 playFill 落定時走 playLand→loop。
    this.startIdleLoops()
  }

  /**
   * 消除後的補牌（對應 Unity FillSymbol）：只補有中獎的欄（fromRow===null 的新符號），自上方掉入；
   * 主遊戲且盤面 Scatter≥2 時先顯示補牌預兆（ani_reel_bg＋等待 1.5s／Turbo 1.0s，可被快停打斷），
   * 且該批欄改逐顆 0.4s 慢速掉入。Tracker 補位格由右滑入。
   */
  async playFill(step: TumbleStep): Promise<void> {
    this.resetForceStop()
    const newRowsByReel = new Map<number, Set<number>>()
    step.fallMap.forEach((fall, reel) => {
      const rows = new Set<number>()
      for (const entry of fall) {
        if (entry.fromRow === null) rows.add(entry.finalRow)
      }
      if (rows.size > 0) newRowsByReel.set(reel, rows)
    })
    const trackerRows = new Set(step.trackerFall.filter((e) => e.fromRow === null).map((e) => e.finalRow))
    if (newRowsByReel.size === 0 && trackerRows.size === 0) return

    // 補牌預兆：主遊戲且盤面（消除後）Scatter ≥ 2；Tracker 有補位時 1~4 欄也亮底光（對照 Unity 頂輪命中處理）。
    const scatters = step.boardAfter.reduce(
      (count, column) => count + column.filter((cell) => !isCovered(cell) && cell.symbol === SCATTER_SYMBOL_ID).length,
      0,
    )
    const fillOmen = !this.freeGameActive && scatters >= ANTICIPATION_SCATTER_COUNT
    if (fillOmen) {
      const omenReels = new Set<number>(newRowsByReel.keys())
      if (trackerRows.size > 0) for (const reel of TRACKER_OVERLAY_REELS) omenReels.add(reel)
      this.showFillOmen(omenReels)
      await this.waitOrForce(this.turboEnabled ? FILL_OMEN_DELAY_TURBO_MS : FILL_OMEN_DELAY_MS)
    }

    const scatterCounter = { count: this.countBoardScatters(step.boardBefore) }
    const tasks: Promise<void>[] = []
    for (const [reel, rows] of newRowsByReel) {
      tasks.push(
        this.fillReelColumn(reel, step.boardAfter[reel]!, {
          groupDelayMs: 0,
          omen: fillOmen,
          scatterCounter,
          revealOnly: rows,
          staggerCells: true,
        }),
      )
    }
    if (trackerRows.size > 0) {
      tasks.push(this.fillTracker(step.trackerAfter, { groupDelayMs: 0, revealOnly: trackerRows, staggerCells: true }))
    }
    await Promise.all(tasks)
    this.hideFillOmen()
  }

  private countBoardScatters(board: Board): number {
    return board.reduce(
      (count, column) => count + column.filter((cell) => !isCovered(cell) && cell.symbol === SCATTER_SYMBOL_ID).length,
      0,
    )
  }

  /**
   * Wild 變身批次（對應 Unity TransformSymbol_Enter）：首次顯示金木框（ani_wildFrame "in"＋se_wildframe），
   * 全部變身格並行播「原符號骨骼 win 1s → change 1s → 換 WW」（SymbolView.playWildTransform），
   * 結束後清除顯示覆寫並等 _changeSymbolDelay=0.4s。
   */
  async playWildTransforms(step: TumbleStep): Promise<void> {
    const transformViews = step.wildTransforms
      .map((t) => this.cells[t.ref.reel]?.[t.ref.row])
      .filter((view): view is SymbolView => view !== undefined)
    if (transformViews.length === 0) return

    if (!this.wildFrameVisible) {
      this.wildFrameVisible = true
      this.bus?.emit('wildFrameShown', undefined)
      this.showWildFrame()
    }
    this.bus?.emit('wildTransformed', { count: transformViews.length })
    await Promise.all(transformViews.map((view) => view.playWildTransform()))
    // 變身完成：各格視圖已自行換成 WW，覆寫失效（下次 renderBoard 直接顯示 boardAfter 的 WW）。
    for (const t of step.wildTransforms) this.displayOverrides.delete(`${t.ref.reel}:${t.ref.row}`)
    await delay(CHANGE_SYMBOL_DELAY_MS)
  }

  /** 顯示 Wild 金木框（ani_wildFrame "in"，播完停在最後一幀）。 */
  private showWildFrame(): void {
    if (this.wildFrameSpine === null) {
      this.wildFrameSpine = createEffectSpine('wildFrame')
      if (this.wildFrameSpine !== null) this.overlayLayer.addChildAt(this.wildFrameSpine, 0)
    }
    const spine = this.wildFrameSpine
    if (spine === null) return
    this.layoutWildFrame(spine)
    spine.visible = true
    if (hasAnimation(spine, EFFECT_ANIM.wildFrameIn)) spine.state.setAnimation(0, EFFECT_ANIM.wildFrameIn, false)
  }

  /** 收起 Wild 金木框（ani_wildFrame "out"；下一次開轉時呼叫，對應 Unity Rotate_Enter 的 ShowChange(false)）。 */
  private hideWildFrame(): void {
    if (!this.wildFrameVisible) return
    this.wildFrameVisible = false
    const spine = this.wildFrameSpine
    if (spine === null) return
    if (hasAnimation(spine, EFFECT_ANIM.wildFrameOut)) {
      spine.state.setAnimation(0, EFFECT_ANIM.wildFrameOut, false)
      const ms = animationDuration(spine, EFFECT_ANIM.wildFrameOut, 0.4) * 1000
      setTimeout(() => {
        if (spine !== null && !spine.destroyed && !this.wildFrameVisible) spine.visible = false
      }, ms)
    } else {
      spine.visible = false
    }
  }
}
